import { clsx } from 'clsx';
import { TabsContent } from 'src/components/ui/tabs';
import React, { useEffect, useRef, useState } from 'react';
import { FullScreenButton } from 'src/components/ButtonWithIcon/FullScreenButton';
import { Controller, useFormContext } from 'react-hook-form';
import { Skeleton } from 'src/components/ui/skeleton';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import {
  SystemPromptTemplateSelect,
  type CustomTemplateOption,
} from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTemplateSelect/SystemPromptTemplateSelect';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';
import type { ConfigurationFormValues } from 'src/pages/ConfigurationPage/hooks/useConfigurationForm';
import type { ConfigurationPrompt, CustomTemplate } from 'src/services/configurationService';
import { CustomConfigurationMessage } from 'src/components/CustomConfigurationMessage/CustomConfigurationMessage';

export const SystemPromptTab = ({
  isCustomActiveSettings = false,
  configurationSettingsPrompt,
  customTemplatesData,
  readonly,
  isLoading,
  fullScreenBtnClick,
  onEditClick = () => {},
}: {
  isCustomActiveSettings?: boolean;
  configurationSettingsPrompt: ConfigurationPrompt | undefined;
  customTemplatesData: CustomTemplate[] | undefined;
  readonly?: boolean;
  isLoading?: boolean;
  fullScreenBtnClick: () => void;
  onEditClick?: () => void;
}) => {
  const [customTemplateOptions, setCustomTemplateOptions] = useState<CustomTemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<CustomTemplateOption | null>(null);
  const [plateInitialValue, setPlateInitialValue] = React.useState('');
  const tabRef = useRef(null);

  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<ConfigurationFormValues>();

  useEffect(() => {
    if (customTemplatesData) {
      const options = customTemplatesData.map((customTemplate) => {
        return {
          id: `${customTemplate.id}`,
          name: customTemplate.name,
          placeholder: customTemplate.prompt_parts[0].placeholder,
          text: customTemplate.prompt_parts[0].template.trim(),
        };
      });

      setCustomTemplateOptions(options);

      if (configurationSettingsPrompt?.is_manual) {
        const promptText = configurationSettingsPrompt.text.trim();
        const placeholder = Object.keys(configurationSettingsPrompt.input_data)[0];

        setValue('text', promptText);
        setPlateInitialValue(promptText);
        setValue('template_id', 'custom');
        setValue('placeholder', placeholder);
        setValue('selected_template_id', configurationSettingsPrompt.template_id);

        return;
      }

      if (options[0]) {
        onCustomTemplateChange(options[0]);
        setValue('template_id', options[0].id);
      }
    }
  }, [configurationSettingsPrompt, customTemplatesData, readonly]);

  useEffect(() => {
    if (selectedTemplate) {
      setValue('selected_template_id', selectedTemplate.id || '');
    }
  }, [selectedTemplate]);

  const onCustomTemplateChange = (option: CustomTemplateOption | undefined) => {
    if (option) {
      setValue('text', option.text);
      setPlateInitialValue(option.text);
      setSelectedTemplate(option);
      setValue('placeholder', option.placeholder);
    }
  };

  return (
    <TabsContent
      ref={tabRef}
      className={clsx(
        'p-0 mt-0 h-full overflow-hidden pr-2',
        readonly && '[&_div>[role="toolbar"]]:hidden',
      )}
      value={'system-prompt'}
    >
      <div className="flex h-full flex-col items-start gap-2.5 w-full">
        <LoaderWrapper
          isLoading={isSubmitting || isLoading}
          loader={
            <div className="flex flex-col w-full gap-2.5">
              <Skeleton className="w-full h-[40px]" />
            </div>
          }
        >
          {isCustomActiveSettings ? (
            <CustomConfigurationMessage />
          ) : (
            <>
              <div className={'w-full flex flex-row items-center justify-between'}>
                <div className={'flex flex-row gap-2.5 items-center'}>
                  <p className="text-sm text-[#7A6BE5] font-bold">Prompt</p>
                  <SystemPromptTemplateSelect
                    readonly={readonly}
                    onChange={onCustomTemplateChange}
                    options={customTemplateOptions}
                  />
                </div>
                <FullScreenButton onClick={fullScreenBtnClick} />
              </div>
              <Controller
                control={control}
                name="text"
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <div className={'h-full w-full pb-14'}>
                    <PlateEditor
                      readOnly={readonly}
                      label={''}
                      onClick={() => {
                        if (readonly) {
                          onEditClick();
                        }
                      }}
                      initialValue={plateInitialValue}
                      resetOnReadOnlyChange={true}
                      onChange={onChange}
                      error={error}
                      containerClassName={'h-[calc(100%-20px)] !overflow-hidden'}
                      className={'w-full max-w-full h-full'}
                    />
                  </div>
                )}
              />
            </>
          )}
        </LoaderWrapper>
      </div>
    </TabsContent>
  );
};
